/* eslint-disable @typescript-eslint/no-use-before-define */
/*
 This code is the intellectual property of Bulb Inc. and other software publishers;
 it may not be altered, copied or disclosed without prior written approval.
 Copyright 2019, Bulb Inc. For more information, please email contact@hellobulb.com.
          _
   _            | | _
  | |         _ | || |
  | |__   _  | || || |__
  | '_ \ | | | || || '_ \
  | (_) || |_| || || (_) |
   \___/  \___/ |_| \___/
 */

// Our main entry point for our module bundler.

// Direct import of these polyfill libraries for Angular to work.
import 'reflect-metadata';

// Awesome cookie library the cookie monster recommended:
import Cookies from 'js-cookie';

// Two options to compile:
// Dynamic <-- We will be using this for now.
// static ( AOT => Ahead of Time compilation, we eventually want to get here )
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';

// Import our Angular Module:
import { NgModuleRef } from '@angular/core';
import { AppConfigService } from './common/services/app-config/app-config.service';
import { AppModule } from './app/app.module';
import { fetchAJSTemplates } from './ajs/app.module.ajs';

// Import our custom fetch for the current user in order to obtain the locale:
import { InitialUserFetchService } from './initial-user-fetch.service';

const JWPLAYER_URL = 'https://cdn.jwplayer.com/libraries/FXhMfV8q.js';
const CKE_VERSION = window.CKEDITOR_VERSION;

/**
 * Pre-load the request for the template cache and JWPlayer script early.
 * These templates are needed before Angular can bootstrap,
 * so we want to fetch them before the app starts.
 *
 * The JWPlayer script is loaded ahead of time to ensure that it is loaded before the JWPlayer component is rendered.
 */
const templateFetchPromise: Promise<void> = fetchAJSTemplates();
const jwplayerFetchPromise: Promise<boolean> = loadJwPlayerScript();

/**
 * Fetch the currently logged in user from the server to provide necessary details for bootstrapping the app.
 */
InitialUserFetchService.init().then(async (user) => {
  window.bulbVars = window.bulbVars || {};
  window.bulbVars.bulbOriginalUser = user;

  // Locale of the currently fetched user:
  let userLocale = user.language.toLowerCase();
  if (isAnonymousUser(user)) {
    userLocale = handleAnonymousUserLocale();
  }
  if (userLocale !== 'en') {
    loadCKETranslations(userLocale);
  }

  // Load critical dependency scripts before bootstrapping the app.
  await Promise.all([templateFetchPromise, jwplayerFetchPromise]);

  // Lets bootstrap!
  bootstrapApp();
});

/**
 * Bootstrap the Angular app.
 */
async function bootstrapApp(compilerOptions = {}): Promise<void> {
  try {
    await platformBrowserDynamic().bootstrapModule(AppModule, compilerOptions);
    console.log('bulb -- share your smarts.');
  } catch (error) {
    console.log('Bootstrapped failed:', error);
  }
}

/**
 * Injects a script tag into the DOM.
 * IMPORTANT: Always resolves, even if the script fails to load.
 * We don't want to block the app from bootstrapping if the script fails to load.
 * Parts of the app may not function correctly if the script fails to load, but the app should still be usable.
 */
function loadScript(src: string): Promise<boolean> {
  return new Promise((resolve) => {
    const script = document.createElement('script');
    script.src = src;
    script.defer = true;
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.head.append(script);
  });
}

/**
 * Prepare script to load JW Player.
 */
function loadJwPlayerScript(): Promise<boolean> {
  return loadScript(JWPLAYER_URL);
}

// Load the CKEditor translations.
function loadCKETranslations(locale: string): Promise<boolean> {
  // Translation bundles are versioned alongside the CKEditor package.
  const CKE_TRANSLATIONS_URL = `https://cdn.ckeditor.com/ckeditor5/48.5.1/translations/${locale}.umd.js`;
  return loadScript(CKE_TRANSLATIONS_URL);
}

/**
 * Utility function that returns true if the current user is anonymous a.k.a not logged in.
 * @param user
 */
function isAnonymousUser(user) {
  return user.id === null;
}

/**
 * Utility function that checks to see if the anonymous user has a language already set. If they don't, we default them
 * to english.
 */
function handleAnonymousUserLocale() {
  const language = Cookies.get('language');
  if (language) {
    return language.toLowerCase();
  }
  // Fallback language:
  return 'en';
}
